/**
 * The storage envelope: a document packed for localStorage, and unpacked again.
 *
 * localStorage meters its quota in UTF-16 code units — about 2.6 million of them
 * on the ~5 MB origins Firefox and WebKit give — and a JSON document spends a
 * whole unit on every ASCII character, which is eight bits of information in a
 * sixteen-bit slot at best and a repeated key name at worst. That is the ceiling
 * `persist.ts` describes under `SESSION_BUDGET_CHARS`, and it is the reason a
 * source-checked exploration stopped saving at about five hundred nodes. The
 * honest long fix — IndexedDB, which has no such ceiling — is D33's option (b)
 * and a different project, because every read and write of the library is
 * synchronous by decision. This file is the fix that fits inside that decision:
 * still synchronous, still a string in the same key, only a much shorter one.
 *
 * Three parts, and each is chosen for a reason that is not "it compresses":
 *
 *  - DEFLATE, raw. A standard rather than a bespoke scheme, because the stored
 *    form has to be readable for as long as anybody keeps an exploration: a
 *    document written today must open in a build years from now, and any
 *    inflate implementation on earth reads RFC 1951.
 *
 *  - A CRC-32 OF THE PLAIN BYTES, carried in the header and checked after
 *    inflating. Raw deflate has no checksum of its own, and the zlib framing's
 *    would not help: the inflate this app ships strips that trailer without
 *    reading it. A value damaged in storage has to fail to unpack rather than
 *    unpack to a document with a different sentence in it — the codec
 *    downstream validates shape, not prose — and checking the plain bytes
 *    covers the inflate as well as the storage.
 *
 *  - FIFTEEN BITS PER CODE UNIT, offset by 0x20. The compressed bytes have to go
 *    back into a string, and how they are spread across code units is what the
 *    quota actually meters. Base64 would spend a sixteen-bit unit on six bits.
 *    Fifteen bits per unit is 94% of the slot, every unit lands in
 *    U+0020..U+801F — below the surrogates, so the string is always well-formed
 *    UTF-16, and above the C0 controls, which some storage layers have mangled.
 *    Chromium meters a Latin-1-only string at one byte per unit and everything
 *    else at two, and this layout still wins there: fifteen bits per two bytes
 *    against base64's six per one.
 *
 * The plain form is still accepted on the way in. Every document written before
 * this file existed starts with `{`, a packed one starts with `PACK_TAG`, and the
 * reader tells them apart at index 0 without parsing either — so nothing here
 * needs a `SCHEMA` bump and nothing already on disk is migrated. A document is
 * repacked the next time it is written, which is the next time it changes.
 *
 * WHAT IS NOT PROMISED: that a packed document is smaller. `packText` returns
 * the plain text whenever packing would not shrink it, cannot address it, or
 * cannot read its own output back, so the worst case of this file is exactly the
 * behaviour before it. The self-check covers the unit layout — the part of this
 * file that is not a standard — and costs a byte compare.
 *
 * Measured on documents built from real prose, the packed form is about a fifth
 * of the plain one, at roughly thirty milliseconds per megabyte of JSON to pack
 * and a third of that to unpack. Packing ran on the main thread inside the
 * autosave debounce, which is why the level below is not the default: it is
 * within 5% of the default's ratio at three quarters of its time.
 *
 * An autosave now runs this file in a worker (`packHost.ts`, `packWorker.ts`);
 * the pagehide checkpoint and the legacy migration still run it here. Either way
 * it is this function, and its output is pinned (`fixtures/packVectors.ts`):
 * the level, the layout and every constant below are what other builds read, so
 * none of them moves for speed without a new `PACK_TAG`.
 */

import { deflateSync, inflateSync } from 'fflate'

/**
 * What a packed document starts with, and what a plain one never does. The digit
 * is the envelope's own version: a later layout takes `§2` and this reader keeps
 * refusing what it cannot read rather than guessing.
 */
export const PACK_TAG = '§1'

/** Deflate level. See the header for why it is not the default 6. */
const LEVEL = 3
/** Payload bits per stored code unit. */
const UNIT_BITS = 15
const UNIT_MASK = (1 << UNIT_BITS) - 1
/** Added to every unit so none lands in the C0 control range. */
const UNIT_BASE = 0x20
/** Code units per `String.fromCharCode` call — comfortably inside every engine's
 *  argument limit, and few enough calls to matter to nobody. */
const CHUNK = 8192
/** The byte count travels in two units of fifteen bits each. */
const MAX_BYTES = 1 << 30
/** Tag, two units of byte count, three units of checksum, then the payload. */
const HEADER_UNITS = PACK_TAG.length + 5

const unit = (v: number): string => String.fromCharCode(v + UNIT_BASE)

/** The unit at `at`, or -1 for anything outside the range the packer writes. */
function unitAt(stored: string, at: number): number {
  const v = stored.charCodeAt(at) - UNIT_BASE
  return v >= 0 && v <= UNIT_MASK ? v : -1
}

const CRC_TABLE = (() => {
  const t = new Int32Array(256)
  for (let n = 0; n < 256; n++) {
    let c = n
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
    t[n] = c
  }
  return t
})()

/** IEEE CRC-32, the one every zip and png carries. */
function crc32(bytes: Uint8Array): number {
  let c = -1
  for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]!) & 0xff]! ^ (c >>> 8)
  return (c ^ -1) >>> 0
}

/** Spread bytes across fifteen-bit code units, the last one zero-padded. */
function unitsOf(bytes: Uint8Array): string {
  const out = new Uint16Array(Math.ceil((bytes.length * 8) / UNIT_BITS))
  let acc = 0
  let bits = 0
  let o = 0
  for (let i = 0; i < bytes.length; i++) {
    acc = (acc << 8) | bytes[i]!
    bits += 8
    if (bits >= UNIT_BITS) {
      out[o++] = ((acc >>> (bits - UNIT_BITS)) & UNIT_MASK) + UNIT_BASE
      bits -= UNIT_BITS
      acc &= (1 << bits) - 1
    }
  }
  if (bits > 0) out[o++] = ((acc << (UNIT_BITS - bits)) & UNIT_MASK) + UNIT_BASE
  let s = ''
  for (let i = 0; i < out.length; i += CHUNK) {
    s += String.fromCharCode(...out.subarray(i, Math.min(i + CHUNK, out.length)))
  }
  return s
}

/**
 * The inverse, strict about everything it can be strict about: the unit count
 * has to be exactly what `n` bytes take, every unit has to be in range, and the
 * padding bits have to be the zeros the packer wrote. Null on any of those —
 * a damaged value is refused here before the checksum ever sees it.
 */
function bytesOf(stored: string, from: number, n: number): Uint8Array | null {
  if (stored.length - from !== Math.ceil((n * 8) / UNIT_BITS)) return null
  const out = new Uint8Array(n)
  let acc = 0
  let bits = 0
  let o = 0
  for (let i = from; i < stored.length; i++) {
    const v = unitAt(stored, i)
    if (v < 0) return null
    acc = (acc << UNIT_BITS) | v
    bits += UNIT_BITS
    while (bits >= 8 && o < n) {
      out[o++] = (acc >>> (bits - 8)) & 0xff
      bits -= 8
    }
    acc &= (1 << bits) - 1
  }
  if (o !== n || acc !== 0) return null
  return out
}

const sameBytes = (a: Uint8Array, b: Uint8Array): boolean => {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false
  return true
}

/** Whether a stored value is in this file's envelope rather than plain JSON. */
export const isPackedText = (stored: string): boolean => stored.startsWith(PACK_TAG)

/**
 * Pack a document for storage. Never throws and never returns something
 * `unpackText` cannot read: on any failure, and whenever packing would not make
 * the value shorter, the plain text comes back and is stored as it always was.
 */
export function packText(plain: string): string {
  try {
    const bytes = new TextEncoder().encode(plain)
    const crc = crc32(bytes)
    const packed = deflateSync(bytes, { level: LEVEL })
    if (packed.length >= MAX_BYTES) return plain
    const stored =
      PACK_TAG +
      unit(Math.floor(packed.length / (UNIT_MASK + 1))) +
      unit(packed.length % (UNIT_MASK + 1)) +
      unit(crc >>> 30) +
      unit((crc >>> 15) & UNIT_MASK) +
      unit(crc & UNIT_MASK) +
      unitsOf(packed)
    if (stored.length >= plain.length) return plain
    // The unit layout, read back and compared byte for byte. Deflate is a
    // standard exercised by every browser; the layout above is this file's own,
    // and the one place a bug would turn a save into an unreadable value.
    const back = bytesOf(stored, HEADER_UNITS, packed.length)
    if (!back || !sameBytes(back, packed)) return plain
    return stored
  } catch {
    return plain
  }
}

/**
 * The document text behind a stored value, whichever form it is in. A plain
 * value is handed back untouched — validating it is the codec's job, not this
 * file's. A packed value that does not decode, does not checksum or is not valid
 * UTF-8 answers null: unreadable, and said so, rather than a document that
 * merely looks like one.
 */
export function unpackText(stored: string): string | null {
  if (!isPackedText(stored)) return stored
  try {
    if (stored.length < HEADER_UNITS) return null
    const h = Array.from({ length: 5 }, (_, i) => unitAt(stored, PACK_TAG.length + i))
    // The checksum's top piece is two bits; anything wider is not a header this
    // packer wrote.
    if (h.some((v) => v < 0) || h[2]! > 3) return null
    const packed = bytesOf(stored, HEADER_UNITS, h[0]! * (UNIT_MASK + 1) + h[1]!)
    if (!packed) return null
    const bytes = inflateSync(packed)
    // Assembled in the same three pieces the packer split it into, and compared
    // as unsigned: `>>> 0` on both sides, because a CRC with its top bit set is
    // negative as a signed 32-bit int and equal to nothing.
    const crc = ((h[2]! << 30) | (h[3]! << 15) | h[4]!) >>> 0
    if (crc32(bytes) !== crc) return null
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes)
  } catch {
    return null
  }
}
